import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * SVG world map with animated pins for each PurpleVPS location.
 * Uses equirectangular projection: x = (lng + 180) * (W/360), y = (90 - lat) * (H/180)
 */
const W = 1000;
const H = 500;

function project(lat, lng) {
  return { x: (lng + 180) * (W / 360), y: (90 - lat) * (H / 180) };
}

export default function WorldMap({ highlight }) {
  const [locations, setLocations] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/public/locations").then((r) => setLocations(r.data.locations));
  }, []);

  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0A0614]/70">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <radialGradient id="glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#EC4899" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="pin" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
          <pattern id="dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.06)" />
          </pattern>
        </defs>

        {/* Backdrop dot grid stylized as a "map" */}
        <rect width={W} height={H} fill="url(#dots)" />

        {/* Continents (very simplified) */}
        {CONTINENTS.map((path, i) => (
          <motion.path
            key={i}
            d={path}
            fill="rgba(124, 58, 237, 0.10)"
            stroke="rgba(124, 58, 237, 0.28)"
            strokeWidth="0.8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.1 * i }}
          />
        ))}

        {/* Connection lines from Frankfurt to every other region */}
        {locations.length > 0 && locations
          .filter((l) => l.code !== "fra")
          .map((l, i) => {
            const from = project(50.11, 8.68);
            const to = project(l.lat, l.lng);
            const midX = (from.x + to.x) / 2;
            const midY = Math.min(from.y, to.y) - Math.abs(to.x - from.x) / 6;
            return (
              <motion.path
                key={l.code}
                d={`M ${from.x} ${from.y} Q ${midX} ${midY} ${to.x} ${to.y}`}
                stroke="url(#pin)"
                strokeWidth="1"
                fill="none"
                strokeOpacity="0.35"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 0.5 + i * 0.08, ease: "easeInOut" }}
              />
            );
          })}

        {/* Pins */}
        {locations.map((l, i) => {
          const { x, y } = project(l.lat, l.lng);
          const isHl = highlight === l.code || active === l.code;
          return (
            <g key={l.code}
              onMouseEnter={() => setActive(l.code)}
              onMouseLeave={() => setActive(null)}
              style={{ cursor: "pointer" }}
              data-testid={`map-pin-${l.code}`}>
              <motion.circle
                cx={x} cy={y} r={22}
                fill="url(#glow)"
                initial={{ scale: 0 }}
                animate={{ scale: isHl ? [1, 1.6, 1] : 1 }}
                transition={{ duration: isHl ? 1.4 : 0.6, delay: 0.4 + i * 0.05, repeat: isHl ? Infinity : 0 }}
              />
              <motion.circle
                cx={x} cy={y} r={5}
                fill="url(#pin)"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + i * 0.05 }}
              />
              <motion.circle
                cx={x} cy={y} r={2.2}
                fill="white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.05 }}
              />
              {isHl && (
                <g>
                  <rect x={x + 10} y={y - 22} width={l.city.length * 7 + 24} height={26} rx={13}
                    fill="rgba(17,24,39,0.9)" stroke="rgba(255,255,255,0.1)" />
                  <text x={x + 22} y={y - 5} fill="white" fontSize="11" fontFamily="ui-monospace, monospace">
                    {l.city} — {l.latency_ms}ms
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Very stylised continent silhouettes — decorative, not geographically precise.
const CONTINENTS = [
  // North America
  "M 150 90 Q 200 60 260 80 L 300 130 L 300 200 L 240 240 L 170 220 L 130 170 Z",
  // South America
  "M 260 260 Q 300 250 320 290 L 315 380 L 285 430 L 255 400 L 250 320 Z",
  // Europe
  "M 460 90 Q 510 80 550 100 L 560 150 L 520 170 L 470 155 L 455 120 Z",
  // Africa
  "M 490 180 Q 540 175 580 210 L 585 320 L 545 380 L 500 340 L 485 260 Z",
  // Asia
  "M 570 90 Q 700 70 820 110 L 850 170 L 810 230 L 720 240 L 640 210 L 590 155 Z",
  // India / SE Asia
  "M 680 220 L 720 260 L 730 300 L 700 320 L 680 280 Z",
  // Australia
  "M 780 340 Q 820 330 860 355 L 855 400 L 810 410 L 785 385 Z",
];
